/**
 * Tabella lotti di un articolo di magazzino.
 *
 * Usata nella pagina ArticoloDetail come contenuto del tab "Lotti".
 * Mostra quantità, date, costo, posizione, esito verifica e stato
 * con colorazione automatica sulla scadenza.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

interface Lotto {
  id: string;
  numero_lotto: string | null;
  quantita_disponibile: number;
  quantita_iniziale: number;
  data_ingresso: string;
  data_scadenza: string | null;
  posizione_id: string | null;
  fornitore_id: string | null;
  stato: string;
  costo_unitario: number | null;
}

interface LottiTableProps {
  /** UUID dell'articolo di magazzino. */
  articoloId: string;
}

/** Soglia in giorni sotto la quale il lotto è considerato prossimo a scadenza. */
const GIORNI_SCADENZA_WARNING = 30;

const AUTH_HEADERS = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/**
 * Calcola i giorni rimanenti alla scadenza a partire dalla data odierna.
 *
 * @param dataScadenza - Data di scadenza in formato ISO
 * @returns Numero di giorni rimanenti (negativo se scaduto)
 */
const calcolaGiorniRimanenti = (dataScadenza: string): number => {
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scadenza = new Date(dataScadenza);
  scadenza.setHours(0, 0, 0, 0);
  const diffMs = scadenza.getTime() - oggi.getTime();
  const MS_PER_DAY = 86400000;
  return Math.ceil(diffMs / MS_PER_DAY);
};

/**
 * Determina la variante del Badge scadenza in base ai giorni rimanenti.
 *
 * @param dataScadenza - Data di scadenza in formato ISO
 * @returns La variante del Badge: destructive (scaduto), secondary (<30gg), outline (ok)
 */
const scadenzaVariant = (
  dataScadenza: string,
): 'destructive' | 'secondary' | 'outline' => {
  const giorni = calcolaGiorniRimanenti(dataScadenza);
  if (giorni <= 0) return 'destructive';
  if (giorni < GIORNI_SCADENZA_WARNING) return 'secondary';
  return 'outline';
};

/**
 * Tabella lotti dell'articolo con posizione e scadenze.
 */
const LottiTable: React.FC<LottiTableProps> = ({
  articoloId,
}: LottiTableProps) => {
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [lotti, setLotti] = useState<Lotto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cache nomi posizioni e fornitori risolti
  const [nomiPosizioni, setNomiPosizioni] = useState<Record<string, string>>({});
  const [nomiFornitori, setNomiFornitori] = useState<Record<string, string>>({});

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('articolo_id', articoloId);
      params.set('limit', '200');
      const response = await fetch(
        `/api/v1/magazzino/lotti?${params.toString()}`,
        { headers: AUTH_HEADERS() },
      );
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Lotto[] = await response.json();
      setLotti(data);

      // Risolvi nomi posizioni (unique IDs)
      const posIds = [...new Set(data.map((l) => l.posizione_id).filter(Boolean))] as string[];
      const posMap: Record<string, string> = {};
      await Promise.all(
        posIds.map(async (pid) => {
          try {
            const r = await fetch(`/api/v1/magazzino/posizioni/${pid}`, {
              headers: AUTH_HEADERS(),
            });
            if (r.ok) {
              const p = await r.json();
              posMap[pid] = p.codice ?? pid;
            }
          } catch { /* ignore */ }
        }),
      );
      setNomiPosizioni(posMap);

      // Risolvi nomi fornitori (unique IDs)
      const fornIds = [...new Set(data.map((l) => l.fornitore_id).filter(Boolean))] as string[];
      const fornMap: Record<string, string> = {};
      await Promise.all(
        fornIds.map(async (fid) => {
          try {
            const r = await fetch(`/api/v1/soggetti/${fid}`, {
              headers: AUTH_HEADERS(),
            });
            if (r.ok) {
              const f = await r.json();
              fornMap[fid] = f.ragione_sociale ?? f.nome ?? fid;
            }
          } catch { /* ignore */ }
        }),
      );
      setNomiFornitori(fornMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [articoloId]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const formatDate = (iso: string): string => fmt.date(iso);

  return (
    <Card>
      <CardContent className="p-0">
        {loading && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Caricamento…
          </p>
        )}
        {error && (
          <p className="p-6 text-center text-sm text-destructive">{error}</p>
        )}
        {!loading && !error && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lotto</TableHead>
                <TableHead className="text-right">Disp. / Iniz.</TableHead>
                <TableHead>Ingresso</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Posizione</TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Costo unit.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lotti.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-sm text-muted-foreground"
                  >
                    Nessun lotto registrato
                  </TableCell>
                </TableRow>
              )}
              {lotti.map((lotto: Lotto) => (
                <TableRow
                  key={lotto.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/magazzino/lotti/${lotto.id}`)}
                >
                  <TableCell className="font-mono text-sm">
                    {lotto.numero_lotto ?? '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {lotto.quantita_disponibile} / {lotto.quantita_iniziale}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(lotto.data_ingresso)}
                  </TableCell>
                  <TableCell>
                    {lotto.data_scadenza ? (
                      <Badge variant={scadenzaVariant(lotto.data_scadenza)}>
                        {formatDate(lotto.data_scadenza)}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {lotto.posizione_id
                      ? nomiPosizioni[lotto.posizione_id] ?? '…'
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {lotto.fornitore_id
                      ? nomiFornitori[lotto.fornitore_id] ?? '…'
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        lotto.stato === 'disponibile' ? 'default' :
                        lotto.stato === 'in_quarantena' ? 'destructive' : 'secondary'
                      }
                    >
                      {lotto.stato}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {lotto.costo_unitario != null
                      ? `\u20AC ${Number(lotto.costo_unitario).toFixed(4)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default LottiTable;
